import * as React from 'react';
import {
  Box,
  Toolbar,
  Stack,
  IconButton,
  Link,
  Paper,
  AppBar,
  Collapse,
  InputBase,
  useTheme,
  ClickAwayListener,
  Typography,
} from '@mui/material'; //Menu, MenuItem
import {Clear, Info, SearchRounded} from '@mui/icons-material';

import {DropMenu} from 'components/DropMenu/DropMenu';
import {
  ITrainingCard,
  IDocumentCard,
  IProcessCard,
  LogoSize,
  IUserProfile,
  BannerType,
  E_QuickLinks,
  LogInType,
} from 'app/types/Shared';

//redux imports
import {useDispatch, useSelector} from 'react-redux';
import {selectUserProfile, setLogOut, setAuthSuccess, setLoading} from 'app/store/auth/authSlice';
import Banner from 'components/Banner/Banner';
import {OnboardingActions, selectOnboarding, setOnboarding, setSelectedGroup} from 'app/store/appState/appStateSlice';
import {useFeatureFlag} from 'configcat-react';

import {useMsal, useIsAuthenticated} from '@azure/msal-react';
import {selectUserType} from 'app/store/users/userSlice';
import {useNavigate} from 'react-router-dom';
import {StyledTooltip} from 'components/StyledTooltip/StyledTooltip';

export enum ResourcesDropDownItems {
  AllResources = 'All Process Resources',
  BusinessControlled = 'Business Controlled',
}

export interface Params {
  authorized: boolean;
  login?: (type: LogInType) => Promise<void>;
  logout?: () => Promise<void>;
  home?: () => Promise<void>;
  search?: (query?: string) => Promise<void>;
  toResources?: () => Promise<void>;
  pinnedMedia?: Array<ITrainingCard | IDocumentCard | IProcessCard>;
  smallLogo: string;
  largeLogo?: string;
  rightLogo?: string;
  LogoType: LogoSize;
  banner?: boolean;
  storybook?: boolean;
  ttdom?: boolean;
  empty?: boolean;
}

const TopNavigationContent = (params: Params) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const s = useSelector(selectUserProfile);
  const userType = useSelector(selectUserType);
  const onboarding = useSelector(selectOnboarding);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const {instance} = useMsal();
  const isAuthenticated = useIsAuthenticated();

  //Responsive Header based on veiwport of landscape tablet (1150px breakpoint)
  const [width, setWidth] = React.useState(window.innerWidth);
  const breakpoint = 1150;
  React.useEffect(() => {
    const handleResizeWindow = () => setWidth(window.innerWidth);
    // subscribe to window resize event "onComponentDidMount"
    window.addEventListener('resize', handleResizeWindow);
    return () => {
      // unsubscribe "onComponentDestroy"
      window.removeEventListener('resize', handleResizeWindow);
    };
  }, []);

  const onCROLogin = async () => {
    if (params.login) {
      await params.login(LogInType.External);
    } else if (params.storybook) {
      // todo: only used for storybook mock, remove for production
      const user: IUserProfile = {
        email: 'cro.user@test.com',
        displayName: 'CRO User',
        firstName: 'CRO',
        lastName: 'User',
        timeZone: 'America/Chicago',
        groups: [],
      };
      dispatch(setAuthSuccess(user));
    }
  };

  const onInternalLogin = async () => {
    if (params.login) {
      await params.login(LogInType.Internal);
    } else if (params.storybook) {
      // todo: only used for storybook mock, remove for production
      const user: IUserProfile = {
        email: 'employee.user@test.com',
        displayName: 'Employee User',
        firstName: 'Employee',
        lastName: 'User',
        timeZone: 'America/Chicago',
        groups: [],
      };
      dispatch(setAuthSuccess(user));
    }
  };

  const onLogout = async () => {
    if (isAuthenticated) {
      dispatch(setLoading(true));
      const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      await instance.logoutRedirect({
        account: account ?? undefined,
        logoutHint: account?.username,
        postLogoutRedirectUri: window.location.origin + '/logout',
      });
      dispatch(setLogOut());
      // trigger additional logout actions from parent
    }
  };

  const handleSearch = async () => {
    setSearchOpen(!searchOpen);
  };

  const handleSearchClickAway = (event: MouseEvent | TouchEvent) => {
    if (searchOpen) {
      const excludedElement = document.getElementById('search-icon');
      const excludedElement2 = document.getElementById('search-icon-button');
      if (excludedElement !== event.target && excludedElement2 !== event.target) {
        setSearchOpen(false);
      }
    }
  };

  const [docQuery, setDocQuery] = React.useState('');

  const handleDocumentSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == 'Enter') {
      params.search && params.search(docQuery);
    }
  };

  const onHome = async () => {
    if (params.home) {
      await params.home();
    }
  };

  const navigateToQuickLink = async (quickLink: string) => {
    if (onboarding?.onboardingModal) {
      dispatch(setOnboarding(OnboardingActions.DisableOnboardingModal));
    }
    switch (quickLink) {
      case E_QuickLinks.TDOM:
        dispatch(setSelectedGroup(quickLink));
        if (params.home) {
          await params.home();
        }
        break;
      case E_QuickLinks.Updates:
        dispatch(setSelectedGroup(quickLink));
        if (params.home) {
          await params.home();
        }
        break;
      case E_QuickLinks.Pinned:
        navigate('/pinned');
        break;
      case E_QuickLinks.Recent:
        navigate('/recent');
        break;
      case E_QuickLinks.Help:
        navigate('/help');
        break;
      case E_QuickLinks.Faq:
        navigate('/faq');
        break;
      default:
        break;
    }
  };

  const navigateToResources = async (resources: string) => {
    if (resources === ResourcesDropDownItems.AllResources && params.toResources) {
      await params.toResources();
    }
  };

  const quickLinks = [{title: E_QuickLinks.Pinned}, {title: E_QuickLinks.Recent}];

  return (
    <Toolbar sx={{width: '100%', padding: 1, display: 'flex', alignItems: 'baseline'}}>
      <Box flexGrow={1} pl={1}>
        <IconButton disableRipple onClick={onHome}>
          {width > breakpoint && params.largeLogo && params.LogoType === LogoSize.Large ? (
            <Stack spacing={2} direction='row'>
              <img width={300} src={params.largeLogo} alt='Elevate large' />
            </Stack>
          ) : (
            <img width={90} src={params.smallLogo} alt='Elevate small' />
          )}
        </IconButton>
      </Box>
      <Stack direction='column'>
        <Stack direction='row' spacing={width > breakpoint ? 4 : 1} alignItems='center'>
          {s.isAuthenticated && !params?.empty ? (
            <>
              <DropMenu title='Quick Links' callback={navigateToQuickLink} items={quickLinks} />
              <Link
                color='grey.900'
                variant='h5'
                fontWeight={400}
                underline='none'
                onClick={() => navigateToResources(ResourcesDropDownItems.AllResources)}
                sx={{'&:hover': {color: 'primary.main'}, cursor: 'pointer'}}
              >
                Resources
              </Link>
              {userType === LogInType.Internal && (
                <DropMenu
                  title='Key Systems'
                  items={[
                    {title: 'Bloom', link: 'https://takeda.edcast.com/'},
                    {title: 'COMPASS', link: 'https://compass.onetakeda.com/trial-management/overview'},
                    {title: 'Veeva', link: 'https://takeda-quality.veevavault.com'},
                  ]}
                />
              )}
              <DropMenu
                title='Support'
                callback={navigateToQuickLink}
                items={[
                  {title: E_QuickLinks.Help},
                  {title: E_QuickLinks.Faq},
                  {
                    title: 'Report an Issue',
                    link:
                      userType === LogInType.External
                        ? 'mailto:DL.Elevate.Support@takeda.com'
                        : 'https://takeda.service-now.com/AskIT?id=nr_sc_cat_item&sys_id=0c7630f947623150172d80bd436d436f',
                  },
                  {title: 'Provide Feedback', href: 'mailto:DL.Elevate.Support@takeda.com'},
                ]}
              />
              <Link
                color='grey.900'
                variant='h5'
                fontWeight={400}
                underline='none'
                onClick={() => onLogout()}
                sx={{'&:hover': {color: 'primary.main'}, cursor: 'pointer'}}
              >
                Logout
              </Link>
              {params.search && (
                <IconButton
                  onClick={handleSearch}
                  sx={{padding: 0}}
                  disableRipple
                  disableFocusRipple
                  id='search-icon-button'
                >
                  <SearchRounded
                    sx={{
                      color: 'black',
                      height: '28px',
                      width: '28px',
                      stroke: 3,
                    }}
                    id='search-icon'
                  />
                </IconButton>
              )}
            </>
          ) : (
            <>
              <Link
                color='grey.900'
                variant='h5'
                underline='none'
                display={params.storybook === true ? 'unset' : 'none'}
                data-testid='InternalLogin'
                onClick={() => onInternalLogin()}
                sx={{'&:hover': {color: 'primary.main'}, cursor: 'pointer'}}
              >
                Employee Login
              </Link>
              <Link
                color='grey.900'
                display='none'
                variant='h5'
                underline='none'
                onClick={() => onCROLogin()}
                sx={{'&:hover': {color: 'primary.main'}, cursor: 'pointer'}}
                data-testid='CRO'
              >
                CRO Login
              </Link>
            </>
          )}
          <Box>
            {params.rightLogo && (
              <IconButton
                href='https://www.takeda.com/'
                target='_blank'
                size='medium'
                sx={{
                  '&:hover': {
                    backgroundColor: 'white',
                    color: 'inherit',
                  },
                  background: 'white',
                  p: 0,
                }}
              >
                <img width={90} src={params.rightLogo} alt='logo placeholder' style={{marginLeft: -2}} />
              </IconButton>
            )}
          </Box>
        </Stack>
        <Collapse in={searchOpen}>
          <ClickAwayListener onClickAway={handleSearchClickAway}>
            <Paper
              component='form'
              sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                width: '85%',
                background: 'white',
                border: `1px solid ${theme.palette.grey[500]}`,
                borderRadius: '4px',
              }}
            >
              <InputBase
                value={docQuery}
                onChange={(e) => setDocQuery(e.target.value)}
                onKeyDown={handleDocumentSearch}
                sx={{ml: 1, flex: 1, p: 0.3, fontWeight: 400, color: 'black', fontFamily: 'sans-serif'}}
                placeholder='Type your Search'
                inputProps={{'aria-label': 'Search'}}
              />
              <IconButton
                disableRipple
                disableFocusRipple
                sx={{padding: 0}}
                onClick={() => {
                  if (docQuery) setDocQuery('');
                  else setSearchOpen(false);
                }}
              >
                <Clear sx={{color: 'black'}} />
              </IconButton>
            </Paper>
          </ClickAwayListener>
        </Collapse>
      </Stack>
    </Toolbar>
  );
};

const TopNavigation = ({
  smallLogo,
  LogoType,
  authorized,
  rightLogo,
  pinnedMedia,
  login,
  home,
  search,
  largeLogo,
  logout,
  toResources,
  banner = false,
  storybook = false,
  ttdom = false,
  empty = false,
}: Params) => {
  const {value: takedaBannerContent} = useFeatureFlag('takeda_content_top_banner', '');
  const {value: croBannerContent} = useFeatureFlag('cro_content_top_banner', '');
  const userType = useSelector(selectUserType);
  const jsonBanner =
    userType === LogInType.Internal
      ? takedaBannerContent
        ? JSON.parse(takedaBannerContent)
        : {}
      : croBannerContent
      ? JSON.parse(croBannerContent)
      : {};

  const dispatch = useDispatch();
  const theme = useTheme();
  const onCloseBanner = () => {
    dispatch(setOnboarding(OnboardingActions.DisableTopBanner));
  };

  const tooltipTDOM = (
    <React.Fragment>
      <Typography variant='body2'>
        {"If you're not sure, ask your manager or visit the "}
        <Link target='_blank' href='https://mytakeda.sharepoint.com/sites/RDFutureFit'>
          Future Fit information center
        </Link>
      </Typography>
    </React.Fragment>
  );

  if (LogoType === LogoSize.Small) {
    return (
      <AppBar
        enableColorOnDark
        color='inherit'
        elevation={0}
        sx={{boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center'}}
      >
        {banner && jsonBanner?.text && (
          <Banner
            type={BannerType.Top}
            title={jsonBanner?.text}
            linkText={jsonBanner?.link_text}
            link={jsonBanner?.link}
            onButtonClick={onCloseBanner}
          />
        )}
        <TopNavigationContent
          smallLogo={smallLogo}
          authorized={authorized}
          rightLogo={rightLogo}
          pinnedMedia={pinnedMedia}
          login={login}
          home={home}
          search={search}
          logout={logout}
          LogoType={LogoType}
          largeLogo={largeLogo}
          toResources={toResources}
          banner={banner}
          storybook={storybook}
          ttdom={ttdom}
          empty={empty}
        />
        {ttdom && (
          <Box
            sx={{
              position: 'absolute',
              top: '100%', // Position below the toolbar
              left: '50%',
              height: '40px',
              transform: 'translateX(-50%)',
              bgcolor: theme.palette.primary.main,
              p: 1,
              borderRadius: '0 0 4px 4px',
            }}
          >
            <Stack alignItems='center' justifyContent='center' width='100%' height='100%' spacing={1} direction='row'>
              <Typography variant='h6' color='white'>
                You are viewing a TTDOM Process
              </Typography>
              {userType === LogInType.Internal && (
                <StyledTooltip title={tooltipTDOM} placement='bottom'>
                  <Info fontSize='small' sx={{color: 'white'}} />
                </StyledTooltip>
              )}
            </Stack>
          </Box>
        )}
      </AppBar>
    );
  } else {
    return (
      <Stack direction='column' width='100%'>
        {banner && jsonBanner?.text && (
          <Banner
            type={BannerType.Top}
            title={jsonBanner?.text}
            linkText={jsonBanner?.link_text}
            link={jsonBanner?.link}
            onButtonClick={onCloseBanner}
          />
        )}
        <Paper sx={{width: '100%'}}>
          <TopNavigationContent
            smallLogo={smallLogo}
            authorized={authorized}
            rightLogo={rightLogo}
            pinnedMedia={pinnedMedia}
            login={login}
            home={home}
            search={search}
            logout={logout}
            LogoType={LogoType}
            largeLogo={largeLogo}
            toResources={toResources}
            banner={banner}
            storybook={storybook}
          />
        </Paper>
      </Stack>
    );
  }
};

export default TopNavigation;
